import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../services/api';
import { setAuth, getAuth, logout } from '../utils/auth';
import toast, { Toaster } from 'react-hot-toast';
import { FiMail, FiLock, FiEye, FiEyeOff, FiHome } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

// ── Helper: checks if a role string is admin-level ───────────────────────────
// Backend may return "Admin", "SuperAdmin", "admin"   handle all variants
const isAdminRole = (role) => {
  if (!role) return false;
  const r = role.toLowerCase();
  return r === 'admin' || r === 'superadmin';
};

// The Trainer role exists on the backend but has no pages yet. Without this
// guard a trainer signs in, gets sent to /user/dashboard, is bounced by
// ProtectedRoute, and loops between there and /login forever.
const isTrainerRole = (role) => (role || '').toLowerCase() === 'trainer';

// ── Ambient orb ───────────────────────────────────────────────────────────────
const Orb = ({ className, animate, transition }) => (
  <motion.div
    className={`absolute rounded-full pointer-events-none ${className}`}
    animate={animate}
    transition={{ ...transition, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }}
  />
);

// ── Mandala ring ──────────────────────────────────────────────────────────────
const Ring = ({ size, dur, rev = false, opacity = 0.13, dashed = false }) => (
  <motion.div
    animate={{ rotate: rev ? -360 : 360 }}
    transition={{ duration: dur, repeat: Infinity, ease: 'linear' }}
    className="absolute rounded-full"
    style={{
      width: size, height: size,
      top: '50%', left: '50%',
      marginLeft: -(size / 2), marginTop: -(size / 2),
      border: `1px ${dashed ? 'dashed' : 'solid'} rgba(242,154,12,${opacity})`,
    }}
  />
);

// ── Tick ──────────────────────────────────────────────────────────────────────
const Tick = () => (
  <motion.div
    initial={{ scale: 0 }} animate={{ scale: 1 }}
    transition={{ type: 'spring', stiffness: 400, damping: 18 }}
    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
    style={{ background: 'rgba(138,154,91,0.22)' }}
  >
    <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
      <path d="M1 3.5L3 5.5L8 1" stroke="#8EB87A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </motion.div>
);

const Cross = () => (
  <motion.div
    initial={{ scale: 0 }}
    animate={{ scale: 1 }}
    transition={{ type: 'spring', stiffness: 400, damping: 18 }}
    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
    style={{ background: 'rgba(226,75,74,0.22)' }}
  >
    <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
      <path d="M1 1L7 7M7 1L1 7" stroke="#E24B4A" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  </motion.div>
);

// ── Custom checkbox ───────────────────────────────────────────────────────────
const Checkbox = ({ checked, onChange }) => (
  <motion.button type="button" whileTap={{ scale: 0.84 }} onClick={() => onChange(!checked)}
    className="w-[18px] h-[18px] rounded-[6px] flex items-center justify-center flex-shrink-0 border-none outline-none cursor-pointer"
    style={{
      background: checked ? 'linear-gradient(135deg,#F29A0C,#FF8200)' : 'rgba(255,255,255,0.1)',
      border: checked ? 'none' : '1.5px solid rgba(255,255,255,0.25)',
      boxShadow: checked ? '0 3px 12px rgba(242,154,12,0.4)' : 'none',
      transition: 'all 0.25s',
    }}
  >
    <AnimatePresence>
      {checked && (
        <motion.svg initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
          transition={{ duration: 0.2 }} width="10" height="8" viewBox="0 0 10 8" fill="none">
          <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </motion.svg>
      )}
    </AnimatePresence>
  </motion.button>
);

// ── Icon circle ───────────────────────────────────────────────────────────────
const IconCircle = ({ icon: Icon, focused, valid }) => (
  <div className="w-[50px] h-full flex items-center justify-center flex-shrink-0">
    <div className="w-8 h-8 rounded-[11px] flex items-center justify-center transition-all duration-300"
      style={{ background: valid ? 'rgba(138,154,91,0.2)' : focused ? 'rgba(242,154,12,0.26)' : 'rgba(242,154,12,0.14)' }}>
      <Icon size={15} style={{ color: valid ? 'rgba(138,154,91,0.9)' : 'rgba(242,154,12,0.9)' }} />
    </div>
  </div>
);

// ── Social button ─────────────────────────────────────────────────────────────
const SocialBtn = ({ children, onClick }) => (
  <motion.button type="button"
    whileHover={{ y: -2, background: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.3)' }}
    whileTap={{ scale: 0.96 }}
    onClick={onClick}
    className="h-[48px] rounded-[16px] flex items-center justify-center gap-2 text-[13px] font-semibold cursor-pointer"
    style={{
      border: '1px solid rgba(255,255,255,0.18)',
      background: 'rgba(255,255,255,0.1)',
      backdropFilter: 'blur(12px)',
      color: 'rgba(12, 12, 12, 0.82)',
      fontFamily: "'Inter', sans-serif",
      transition: 'all 0.28s',
    }}
  >{children}</motion.button>
);

// ── Home button ───────────────────────────────────────────────────────────────
const HomeButton = ({ navigate }) => (
  <motion.button
    type="button"
    onClick={() => navigate('/')}
    whileHover={{ y: -2, background: 'rgba(255,255,255,0.22)', color: '#fff' }}
    whileTap={{ scale: 0.95 }}
    className="auth-home absolute top-5 left-5 z-20 flex items-center gap-2 rounded-full border cursor-pointer"
    style={{
      background: 'rgba(255,255,255,0.14)',
      border: '1px solid rgba(255,255,255,0.22)',
      padding: '8px 16px',
      fontSize: 12, fontWeight: 600,
      color: 'rgba(255,255,255,0.75)',
      fontFamily: "'Inter', sans-serif",
      backdropFilter: 'blur(12px)',
      transition: 'all 0.25s cubic-bezier(0.25,1,0.5,1)',
    }}
  >
    <FiHome size={15} style={{ color: 'inherit' }} />
    Home
  </motion.button>
);

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get('redirect') || sessionStorage.getItem('auth_redirect');

  const [form, setForm] = useState({ emailOrUserName: '', password: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [pwFocused, setPwFocused] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);

  // ── FIX 1: case-insensitive role check on already-logged-in redirect ────────
  useEffect(() => {
    const auth = getAuth();
    if (auth.token && !isTrainerRole(auth.role)) {
      if (isAdminRole(auth.role)) {
        navigate('/admin/dashboard');
      } else {
        const dest = redirectParam || '/user/dashboard';
        if (redirectParam) sessionStorage.removeItem('auth_redirect');
        navigate(dest);
      }
    }
  }, [navigate, redirectParam]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    setError('');
    setLoginFailed(false);
    setLoginSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.emailOrUserName || !form.password) {
      setError('Please fill in all fields to continue.');
      return;
    }

    // Ensure any previous or stale auth tokens are cleared before initiating new login
    logout();

    setLoading(true);
    try {
      // /auth/user/login checks both Users and Admins tables on the backend
      const res = await api.post('/auth/user/login', form);

      if (res.data.success) {
        setLoginSuccess(true);
        setLoginFailed(false);

        console.log("LOGIN RESPONSE", res.data);
        console.log("LOGIN DATA", res.data.data);
        const role = res.data.data.role || res.data.data.Role;

        // Trainer authenticates fine against the API, but there is no trainer UI
        // yet. Stop here rather than storing a session that no route accepts.
        if (isTrainerRole(role)) {
          setLoginSuccess(false);
          toast('Trainer sign-in is not available in the web app yet.', { icon: 'ℹ️' });
          setLoading(false);
          return;
        }

        setAuth(res.data.token, res.data.data, role, res.data.data.sessionToken);

        // ── FIX 3: case-insensitive admin detection ───────────────────────────
        if (isAdminRole(role)) {
          toast.success('Welcome, Admin! Redirecting to dashboard…');
          setTimeout(() => navigate('/admin/dashboard'), 900);
        } else {
          toast.success('Welcome back! Redirecting…');
          const dest = redirectParam || '/user/dashboard';
          if (redirectParam) sessionStorage.removeItem('auth_redirect');
          setTimeout(() => navigate(dest), 900);
        }
      }
    } catch {
      setLoginSuccess(false);
      setLoginFailed(true);
      toast.error('Invalid username/email or password.');
      setError('Invalid username/email or password.');
    } finally {
      setLoading(false);
    }
  };

  // ── Field styling helpers ─────────────────────────────────────────────────
  const fBorder = (focused, valid) =>
    error ? 'rgba(226,75,74,0.55)'
      : focused ? 'rgba(242,154,12,0.65)'
        : valid ? 'rgba(138,154,91,0.55)'
          : 'rgba(255,255,255,0.18)';

  const fShadow = (focused, valid) =>
    error ? '0 0 0 3px rgba(226,75,74,0.1)'
      : focused ? '0 0 0 3px rgba(242,154,12,0.12), 0 4px 18px rgba(242,154,12,0.1)'
        : valid ? '0 0 0 3px rgba(138,154,91,0.1)'
          : '0 2px 10px rgba(0,0,0,0.08)';

  const fBg = (focused) =>
    focused ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.12)';

  return (
    <div className="flex overflow-hidden" style={{ minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>

      <Toaster position="top-center" toastOptions={{
        duration: 4000,
        style: { borderRadius: 16, background: '#fff', color: '#111', border: '1px solid #F29A0C', padding: 16, fontFamily: "'Poppins', sans-serif" },
      }} />

      {/* ════════════════════════════════════════════
          LEFT PANEL   Cinematic yoga hero
      ════════════════════════════════════════════ */}
      <div className="relative hidden lg:flex flex-col flex-1 overflow-hidden" style={{ minHeight: '100vh' }}>
        <div className="absolute inset-0" style={{ background: '#090600' }} />
        <img src="/assets/image 25.jpeg" alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 1.0, filter: 'saturate(0.55) brightness(0.65)', transform: 'scale(1.05)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(140deg,rgba(242,154,12,0.16) 0%,rgba(8,5,0,0.14) 38%,rgba(3,2,0,0.22) 100%)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 52% 42%,transparent 22%,rgba(0,0,0,0.7) 100%)' }} />
        <div className="absolute inset-0" style={{ opacity: 0.035, backgroundImage: 'linear-gradient(rgba(242,154,12,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(242,154,12,0.5) 1px,transparent 1px)', backgroundSize: '58px 58px' }} />

        {/* Mandala */}
        <div className="absolute" style={{ top: '50%', left: '52%', transform: 'translate(-50%,-50%)' }}>
          <Ring size={480} dur={90} dashed opacity={0.06} />
          <Ring size={380} dur={60} opacity={0.09} />
          <Ring size={270} dur={38} rev opacity={0.13} />
          <Ring size={175} dur={24} opacity={0.19} />
          <Ring size={95} dur={15} rev opacity={0.28} />
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
            className="absolute" style={{ width: 280, height: 280, top: '50%', left: '50%', marginLeft: -140, marginTop: -140 }}>
            <motion.div animate={{ scale: [1, 1.4, 1] }} transition={{ duration: 2.5, repeat: Infinity }}
              className="absolute rounded-full" style={{ width: 5, height: 5, background: '#F29A0C', top: 0, left: '50%', marginLeft: -2.5, opacity: 0.75 }} />
          </motion.div>
          <motion.div animate={{ rotate: -360 }} transition={{ duration: 38, repeat: Infinity, ease: 'linear' }}
            className="absolute" style={{ width: 280, height: 280, top: '50%', left: '50%', marginLeft: -140, marginTop: -140 }}>
            <div className="absolute rounded-full" style={{ width: 3.5, height: 3.5, background: '#F5C070', bottom: 0, left: '50%', marginLeft: -1.75, opacity: 0.6 }} />
          </motion.div>
        </div>

        {/* Glow orbs */}
        <Orb className="w-[360px] h-[360px] -top-24 -left-24" style={{ background: 'rgba(242,154,12,0.25)', filter: 'blur(100px)' }}
          animate={{ x: [0, 20, 0], y: [0, -26, 0] }} transition={{ duration: 14 }} />
        <Orb className="w-[280px] h-[280px] -bottom-16 -right-12" style={{ background: 'rgba(138,154,91,0.14)', filter: 'blur(100px)' }}
          animate={{ x: [0, -16, 0], y: [0, 20, 0] }} transition={{ duration: 18 }} />
        <Orb className="w-[160px] h-[160px]" style={{ background: 'rgba(255,155,30,0.18)', filter: 'blur(80px)', top: '36%', right: '10%' }}
          animate={{ x: [0, 12, 0], y: [0, -14, 0] }} transition={{ duration: 10 }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between h-full p-[52px]">
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12, duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full px-4 py-[7px] mb-7"
              style={{ background: 'rgba(242,154,12,0.11)', border: '1px solid rgba(242,154,12,0.26)' }}>
              <motion.div animate={{ scale: [1, 1.5, 1] }} transition={{ duration: 2, repeat: Infinity }}
                className="w-1.5 h-1.5 rounded-full" style={{ background: '#F29A0C' }} />
              <span className="text-[11px] font-bold uppercase tracking-[1.6px]" style={{ color: '#F5C070' }}>International Yoga Platform</span>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
              <h2 className="font-black text-white leading-[1.03]"
                style={{ fontFamily: "'Poppins',sans-serif", fontSize: 'clamp(70px,3.5vw,68px)', letterSpacing: '1.1px' }}>
                Transform Your <br />
                <em className="not-italic" style={{ color: '#F29A0C' }}>Mind, Body</em><br />
                &amp; Soul
              </h2>
              <motion.div initial={{ width: 0 }} animate={{ width: 52 }} transition={{ delay: 0.78, duration: 0.9, ease: 'easeOut' }}
                className="h-0.5 rounded-full mt-5" style={{ background: 'linear-gradient(90deg,#F29A0C,transparent)' }} />
            </motion.div>

            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.48, duration: 0.6 }}
              className="mt-5 text-[14px] leading-[1.78]" style={{ color: 'rgb(233, 220, 220)', maxWidth: 350 }}>
              Join thousands of practitioners worldwide on a deeply personal path to wellness guided by expert teachers, live sessions, and science backed nutrition plans.
            </motion.p>

            {/* Stats */}
            <div className="flex items-center mt-10">
              {[['1K', 'Members', 0.7], ['10', 'Programs', 0.82], ['10', 'Trainers', 0.94], ['15', 'Countries', 1.06]].map(([n, l, d], i, a) => (
                <React.Fragment key={l}>
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: d, duration: 0.6 }}
                    className="text-center px-[22px] first:pl-0"
                    style={{ borderRight: i < a.length - 1 ? '1px solid rgba(255,255,255,0.09)' : 'none' }}>
                    <div className="font-black text-white leading-none" style={{ fontFamily: "'Poppins',sans-serif", fontSize: 40, letterSpacing: '-1px' }}>
                      {n}<span style={{ color: '#F29A0C' }}>+</span>
                    </div>
                    <div className="text-[10px] font-semibold uppercase tracking-[.9px] mt-1" style={{ color: 'rgba(255,255,255,0.35)' }}>{l}</div>
                  </motion.div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Trust chips */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.08, duration: 0.6 }}
            className="flex gap-2 flex-wrap">
            {[['🛡', 'SSL Secured'], ['🏅', 'Certified'], ['🌍', 'Global'], ['🔒', 'Private']].map(([e, l]) => (
              <div key={l} className="flex items-center gap-1.5 px-6 py-3 rounded-full text-[11px] font-medium"
                style={{ background: 'rgba(255,255,255,0.055)', border: '1px solid rgba(255,255,255,0.09)', color: 'rgba(237, 225, 225, 0.83)', backdropFilter: 'blur(6px)' }}>
                <span>{e}</span> {l}
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ════════════════════════════════════════════
          RIGHT PANEL   Glass card on dark
      ════════════════════════════════════════════ */}
      <div className="auth-panel relative flex items-center justify-center flex-1 px-9 py-10 overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #ffb027e0,rgba(154, 102, 6, 0.38),rgba(245, 168, 13, 0.49))', minWidth: 680 }}>

        <Orb className="w-[280px] h-[280px] -top-20 -right-16" style={{ background: 'rgba(242,154,12,0.055)', filter: 'blur(72px)' }}
          animate={{ x: [0, 14, 0], y: [0, -18, 0] }} transition={{ duration: 13 }} />
        <Orb className="w-[200px] h-[200px] -bottom-12 -left-8" style={{ background: 'rgba(138,154,91,0.045)', filter: 'blur(60px)' }}
          animate={{ x: [0, -12, 0], y: [0, 14, 0] }} transition={{ duration: 16 }} />
        <div className="absolute inset-0 pointer-events-none"
          style={{ opacity: 0.022, backgroundImage: 'radial-gradient(circle,rgba(255,200,100,0.5) 1px,transparent 1px)', backgroundSize: '28px 28px' }} />

        <HomeButton navigate={navigate} />

        {/* ── GLASS CARD ── */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ y: -5 }}
          className="auth-card relative w-full z-10"
          style={{
            maxWidth: 600,
            background: 'rgba(255,248,235,0.18)',
            borderTop: '1px solid rgba(255,255,255,0.42)',
            borderLeft: '1px solid rgba(255,255,255,0.32)',
            borderRight: '1px solid rgba(255,255,255,0.18)',
            borderBottom: '1px solid rgba(255,255,255,0.14)',
            borderRadius: 32,
            backdropFilter: 'blur(44px) saturate(1.6)',
            WebkitBackdropFilter: 'blur(44px) saturate(1.6)',
            boxShadow: '0 2px 0 rgba(255,255,255,0.55) inset, 0 28px 80px rgba(242,154,12,0.14), 0 8px 30px rgba(0,0,0,0.14)',
            padding: '36px 34px',
            transition: 'box-shadow 0.45s',
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-[1px] rounded-t-[32px]"
            style={{ background: 'linear-gradient(90deg,transparent 5%,rgba(255,255,255,0.75) 50%,transparent 95%)' }} />
          <div className="absolute -top-16 -right-12 w-52 h-52 rounded-full pointer-events-none"
            style={{ background: 'rgba(242,154,12,0.12)', filter: 'blur(80px)' }} />

          {/* Logo */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
            className="flex justify-center mb-5">
            <motion.div whileHover={{ rotate: 7, scale: 1.1 }} whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 320, damping: 18 }}
              className="w-[68px] h-[68px] rounded-[20px] flex items-center justify-center relative overflow-hidden cursor-default"
              style={{ background: '#F29A0C', border: '2px solid rgba(255,255,255,0.7)', boxShadow: '0 14px 40px rgba(242,154,12,0.5),0 3px 10px rgba(0,0,0,0.1)' }}>
              <img src="/assets/logo.jpeg" alt="Divyyog Foundation" className="w-full h-full object-cover" />
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(135deg,rgba(255,255,255,0.22),transparent 55%)' }} />
            </motion.div>
          </motion.div>

          {/* Headline */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}
            className="text-center mb-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] mb-2.5" style={{ color: 'rgb(255, 255, 255)', fontFamily: "'Inter',sans-serif", textShadow: '0 0 20px rgba(242,154,12,0.3)' }}>
              Divyyog Foundation
            </p>
            <h1 className="auth-title font-black leading-[1.04]" style={{ fontFamily: "'Poppins',sans-serif", fontSize: 38, color: 'rgba(255,255,255,0.95)', letterSpacing: '4px', textShadow: '0 1px 12px rgba(0,0,0,0.18)' }}>
              Welcome <span style={{ color: '#000' }}>Back</span>
            </h1>
            <p className="text-[13px] mt-2" style={{ color: '#000', lineHeight: 1.6 }}>
              Continue Your Transformational Journey
            </p>
          </motion.div>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.div key="err"
                initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                animate={{ opacity: 1, height: 'auto', marginBottom: 14, x: [0, -7, 7, -5, 5, 0] }}
                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.38 }}
                className="rounded-[14px] flex items-center gap-2.5 px-4 py-3"
                style={{ border: '1px solid rgba(226,75,74,0.3)', background: 'rgba(226,75,74,0.12)', backdropFilter: 'blur(12px)' }}>
                <div className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-black"
                  style={{ background: 'rgba(217,45,32,0.2)', color: '#ffffff' }}>!</div>
                <span className="text-[12px] leading-[1.4]" style={{ color: '#ffffff', fontFamily: "'Inter',sans-serif" }}>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form */}
          <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.38, duration: 0.6 }}
            onSubmit={handleSubmit} className="space-y-3">

            {/* Email / Username */}
            <div className="auth-field relative flex items-center h-[60px] rounded-[18px] overflow-hidden transition-all duration-300"
              style={{ background: fBg(emailFocused), border: `1px solid ${fBorder(emailFocused, loginSuccess)}`, boxShadow: fShadow(emailFocused, loginSuccess) }}>
              <div className="absolute inset-0 transition-opacity duration-350 pointer-events-none"
                style={{ opacity: emailFocused ? 1 : 0, background: 'radial-gradient(ellipse at 8% 50%,rgba(242,154,12,0.10),transparent 52%)' }} />
              <div className="absolute top-0 left-0 right-0 h-[1px] transition-opacity duration-320 pointer-events-none"
                style={{ opacity: emailFocused ? 1 : 0, background: 'linear-gradient(90deg,transparent,rgba(242,154,12,0.55),transparent)' }} />
              <IconCircle icon={FiMail} focused={emailFocused} valid={loginSuccess} />
              <input type="text" name="emailOrUserName" value={form.emailOrUserName} onChange={handleChange}
                onFocus={() => setEmailFocused(true)} onBlur={() => setEmailFocused(false)}
                placeholder="Username or Email"
                className="flex-1 h-full bg-transparent border-none outline-none font-medium"
                style={{ fontSize: 14, color: 'rgba(17, 16, 16, 0.92)', fontFamily: "'Inter',sans-serif" }} />
              <AnimatePresence>
                {loginSuccess && (<div className="mr-3"><Tick /></div>)}
                {loginFailed && (<div className="mr-3"><Cross /></div>)}
              </AnimatePresence>
            </div>

            {/* Password */}
            <div className="auth-field relative flex items-center h-[60px] rounded-[18px] overflow-hidden transition-all duration-300"
              style={{ background: fBg(pwFocused), border: `1px solid ${fBorder(pwFocused, loginSuccess)}`, boxShadow: fShadow(pwFocused, loginSuccess) }}>
              <div className="absolute inset-0 transition-opacity duration-350 pointer-events-none"
                style={{ opacity: pwFocused ? 1 : 0, background: 'radial-gradient(ellipse at 8% 50%,rgba(242,154,12,0.10),transparent 52%)' }} />
              <div className="absolute top-0 left-0 right-0 h-[1px] transition-opacity duration-320 pointer-events-none"
                style={{ opacity: pwFocused ? 1 : 0, background: 'linear-gradient(90deg,transparent,rgba(242,154,12,0.55),transparent)' }} />
              <IconCircle icon={FiLock} focused={pwFocused} valid={loginSuccess} />
              <input type={showPassword ? 'text' : 'password'} name="password" value={form.password} onChange={handleChange}
                onFocus={() => setPwFocused(true)} onBlur={() => setPwFocused(false)}
                placeholder="Password"
                className="flex-1 h-full bg-transparent border-none outline-none font-medium"
                style={{ fontSize: 14, color: 'rgba(17, 16, 16, 0.92)', fontFamily: "'Inter',sans-serif" }} />
              <div className="mr-3 flex items-center gap-2">
                <AnimatePresence>
                  {loginSuccess && <Tick />}
                  {loginFailed && <Cross />}
                </AnimatePresence>
                <motion.button type="button" whileTap={{ scale: 0.84 }} onClick={() => setShowPassword(!showPassword)}
                  className="bg-transparent border-none cursor-pointer flex items-center"
                  style={{ color: showPassword ? 'rgba(242,154,12,0.85)' : 'rgba(52, 51, 51, 0.95)', fontSize: 16, transition: 'color 0.2s' }}>
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </motion.button>
              </div>
            </div>

            {/* Options */}
            <div className="auth-options flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none" onClick={() => setRememberMe(!rememberMe)}>
                <Checkbox checked={rememberMe} onChange={setRememberMe} />
                <span className="text-[13px]" style={{ color: 'rgba(21, 20, 20, 0.5)' }}>Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-[13px] font-semibold transition-colors duration-200"
                style={{ color: 'rgba(242,154,12,0.85)', fontFamily: "'Inter',sans-serif" }}
                onMouseEnter={e => e.target.style.color = '#F29A0C'}
                onMouseLeave={e => e.target.style.color = 'rgba(242,154,12,0.85)'}>
                Forgot Password?
              </Link>
            </div>

            {/* CTA */}
            <div className="pt-1.5">
              <motion.button type="submit" disabled={loading}
                whileHover={!loading ? { y: -3, scale: 1.012 } : {}}
                whileTap={!loading ? { scale: 0.97 } : {}}
                className="auth-cta group relative overflow-hidden w-full h-[62px] rounded-[20px] border-none outline-none cursor-pointer text-white font-black flex items-center justify-center gap-3"
                style={{
                  fontFamily: "'Poppins',sans-serif", fontSize: 15, letterSpacing: '-0.2px',
                  background: loading ? 'linear-gradient(135deg,#9A6A08,#B07510)' : 'linear-gradient(135deg,#F29A0C,#FF9000,#E87000)',
                  boxShadow: loading ? 'none' : '0 14px 44px rgba(242,154,12,0.45),0 3px 14px rgba(242,154,12,0.28),inset 0 1px 0 rgba(255,255,255,0.32)',
                  transition: 'box-shadow 0.35s', opacity: loading ? 0.7 : 1,
                }}>
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-all duration-[820ms]"
                  style={{ background: 'linear-gradient(110deg,transparent 20%,rgba(255,255,255,0.26) 50%,transparent 80%)', transform: 'skewX(14deg)' }} />
                <div className="absolute top-0 left-0 right-0 h-[1px]" style={{ background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.65),transparent)', opacity: 0.7 }} />
                <div className="absolute bottom-0 left-0 right-0 h-[1px]" style={{ background: 'linear-gradient(90deg,transparent,rgba(0,0,0,0.18),transparent)' }} />
                <span className="relative z-20">{loading ? 'Signing in…' : 'Enter Your Journey'}</span>
                <span className="relative z-20 flex items-center">
                  {loading
                    ? <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    : <motion.span animate={{ x: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}>→</motion.span>
                  }
                </span>
              </motion.button>
            </div>
          </motion.form>

          {/* OR */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.56, duration: 0.6 }}
            className="flex items-center gap-3 my-5">
            <div className="flex-1 h-[1px]" style={{ background: 'rgba(255,255,255,0.12)' }} />
            <span className="text-[11px] font-semibold uppercase tracking-[0.5px]" style={{ color: 'rgba(30, 28, 28, 1.0)' }}>or continue with</span>
            <div className="flex-1 h-[1px]" style={{ background: 'rgba(255,255,255,0.12)' }} />
          </motion.div>

          {/* Social */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6 }}
            className="grid grid-cols-2 gap-2.5 mb-4 text-black">
            <SocialBtn onClick={() => toast('Google login coming soon!')}>
              <svg width="17" height="17" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66 2.84z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Google
            </SocialBtn>
            <SocialBtn onClick={() => toast('Apple login coming soon!')}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="rgba(255,255,255,0.85)">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.31.07 2.21.74 2.98.8.92-.2 1.83-.88 3.16-.94 1.6.07 2.81.74 3.59 1.9-3.3 1.97-2.75 6.3.74 7.54-.42 1.15-.97 2.3-2.47 3.58zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              Apple
            </SocialBtn>
          </motion.div>

          {/* Register card */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.64, duration: 0.6 }}
            whileHover={{ y: -2, background: 'rgba(255,255,255,0.12)', borderColor: 'rgba(242,154,12,0.3)' }}
            className="rounded-[22px] p-[18px] relative overflow-hidden transition-all duration-300"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.16)', backdropFilter: 'blur(12px)' }}>
            <div className="absolute top-0 left-0 right-0 h-[1px]"
              style={{ background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent)' }} />
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-2 text-[10px] font-bold uppercase tracking-[1px]"
              style={{ background: 'rgba(242,154,12,0.14)', border: '1px solid rgba(242,154,12,0.25)', color: 'rgba(242,154,12,0.9)' }}>
              <motion.div animate={{ scale: [1, 1.5, 1] }} transition={{ duration: 2.2, repeat: Infinity }}
                className="w-[5px] h-[5px] rounded-full" style={{ background: '#F29A0C' }} />
              New Here
            </div>
            <p className="font-black text-[14px] mb-1" style={{ fontFamily: "'Poppins',sans-serif", color: 'rgba(16, 16, 16, 0.9)' }}>
              New to Divyyog?
            </p>
            <p className="text-[12px] mb-3 leading-[1.58]" style={{ color: 'rgba(13, 12, 12, 0.88)' }}>
              Create your wellness account and begin your transformational journey free to start.
            </p>
            <Link to="/register">
              <motion.div whileHover={{ y: -2, boxShadow: '0 10px 28px rgba(138,154,91,0.42)' }} whileTap={{ scale: 0.97 }}
                className="w-full h-[44px] rounded-[14px] flex items-center justify-center gap-2 text-white text-[13px] font-black cursor-pointer"
                style={{
                  background: 'linear-gradient(135deg,rgba(138,154,91,0.7),rgba(158,173,109,0.7))',
                  backdropFilter: 'blur(8px)', border: '1px solid rgba(138,154,91,0.4)',
                  boxShadow: '0 6px 20px rgba(138,154,91,0.3)',
                  fontFamily: "'Poppins',sans-serif",
                }}>
                Create Account →
              </motion.div>
            </Link>
          </motion.div>

          <p className="text-center text-[12px] mt-6 leading-relaxed" style={{ color: 'rgba(21, 20, 20, 0.45)', fontFamily: "'Inter',sans-serif" }}>
            By signing in, you agree to our <Link to="/terms-conditions" className="underline hover:text-[#F29A0C] transition-colors duration-200">Terms & Conditions</Link> and <Link to="/privacy-policy" className="underline hover:text-[#F29A0C] transition-colors duration-200">Privacy Policy</Link>.
          </p>

        </motion.div>
      </div>
    </div>
  );
};

export default Login;